<template>
  <div class="students-section" v-loading="studentsLoading" element-loading-text="加载中..." element-loading-spinner="el-icon-loading" element-loading-background="rgba(0, 0, 0, 1)">
    <div class="students-grid">
      <el-button
          v-for="student in sortedStudents"
          :key="student.id"
          :class="getStudentButtonClass(student)"
          @click="onToggleStudentStatus(student)"
          @mousedown.native="onRecordPressStart(student, $event)"
          @mouseup.native="onRecordPressEnd($event)"
          @contextmenu.native.prevent="onShowContextMenu($event, student)"
          class="student-btn"
          :style="getStudentButtonStyle()">
        <span style="text-align: center; width: 100%; display: inline-block;">{{ student.studentNumber }}.{{ student.name }}</span>
        <div v-if="showUnfinishedCount && showUnfinishedCountBadge && studentUnfinishedCounts[student.id] && studentUnfinishedCounts[student.id] > 0"
             class="unfinished-badge">
          {{ studentUnfinishedCounts[student.id] > 99 ? '99+' : studentUnfinishedCounts[student.id] }}
        </div>
      </el-button>
    </div>
    
    <!-- 全局Popover用于右键菜单 -->
    <el-popover
        placement="bottom"
        trigger="manual"
        v-model="contextMenuVisible"
        ref="contextMenu">
      <div style="padding: 5px; cursor: pointer;" @click="checkStudentHistory()">彻查作业</div>
    </el-popover>
  </div>
</template>

<script>
import EventBus from '../../utils/EventBus'

export default {
  name: 'StudentGrid',
  props: {
    sortedStudents: {
      type: Array,
      default: () => []
    },
    showUnfinishedCount: {
      type: Boolean,
      default: false
    },
    studentUnfinishedCounts: {
      type: Object,
      default: () => ({})
    },
    correctionRecords: {
      type: Array,
      default: () => []
    },
    currentDate: {
      type: String,
      default: ''
    },
    selectedType: {
      type: Number,
      default: 1
    },
    classId: {
      type: Number,
      default: null
    },
    subjectIndex: {
      type: Number,
      default: 0
    },
    homeworkTypes: {
      type: Array,
      default: () => []
    },
    isAssigned: {
      type: Boolean,
      default: true
    },
    studentsLoading: {
      type: Boolean,
      default: false
    },
    showUnfinishedCountBadge: {
      type: Boolean,
      default: true
    },
    showContextMenu: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    // 创建一个计算属性来确保响应式更新
    reactiveCorrectionRecords() {
      // 通过访问数组长度来触发响应式更新
      return this.correctionRecords.slice()
    }
  },
  data() {
    return {
      contextMenuVisible: false, // 控制全局popover的显示
      mousePosition: {x: 0, y: 0}, // 鼠标位置
      selectedStudent: null, // 当前选中的学生
      // popover相关
      globalClickListener: null, // 全局点击监听器
      // 长按相关状态
      longPressTimer: null,
      isLongPressTriggered: false // 标志位，用于区分长按和短按
    }
  },
  methods: {
    getStudentButtonClass(student) {
      if (!this.isAssigned) {
        return 'unassigned';
      }
      const record = this.reactiveCorrectionRecords.find(r => r.studentId === student.id);
      return record && record.corrected === true ? 'finished' : 'unfinished';
    },
    getStudentButtonStyle() {
      return {
        height: '70px',
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#333',
        width: '100%',
        boxSizing: 'border-box',
        padding: '8px',
        textAlign: 'center',
        margin: '0',
        borderRadius: '15px',
        boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    },
    onToggleStudentStatus(student) {
      // 如果触发了长按，则不执行点击操作
      if (this.isLongPressTriggered) {
        this.isLongPressTriggered = false // 重置标志
        return
      }

      // 如果未布置作业，提示用户
      if (!this.isAssigned) {
        this.$message.warning('请先点击布置作业按钮！');
        EventBus.$emit('trigger-assign-homework-flash');
        return;
      }

      // 正常的点击逻辑
      let record = this.correctionRecords.find(r => r.studentId === student.id)

      if (!record) {
        // 创建新记录
        record = {
          date: this.currentDate,
          studentId: student.id,
          homeworkTypeId: this.selectedType,
          subjectId: this.getSubjectIdByHomeworkType(this.selectedType),
          corrected: true
        }

        // 如果有班级ID，添加到记录中
        if (this.classId) {
          record.classId = this.classId
        }

        this.$http.post('/correctionRecords', record)
            .then(response => {
              console.log('[StudentGrid] POST /correctionRecords 成功，返回数据:', response.data)
              // 更新本地记录
              this.$emit('update:correctionRecords', [...this.correctionRecords, response.data])
              // 通过 EventBus 通知 StatisticsPanel 更新统计数据
              EventBus.$emit('student-status-changed', {
                action: 'added',
                showUnfinishedCount: this.showUnfinishedCount
              })
              console.log('[StudentGrid] 已发出 student-status-changed 事件')
            })
            .catch(error => {
              console.error('[StudentGrid] 学生状态更新失败', error);
            })
      } else {
        // 更新现有记录
        record.corrected = !record.corrected
        // 如果有班级ID，添加到记录中
        if (this.classId) {
          record.classId = this.classId
        }

        this.$http.post('/correctionRecords', record)
            .then(response => {
              console.log('[StudentGrid] POST /correctionRecords 成功，返回数据:', response.data)
              // 更新本地记录
              const index = this.correctionRecords.findIndex(r => r.studentId === record.studentId)
              if (index > -1) {
                const newRecords = [...this.correctionRecords]
                newRecords[index] = response.data
                this.$emit('update:correctionRecords', newRecords)
              }
              // 通过 EventBus 通知 StatisticsPanel 更新统计数据
              EventBus.$emit('student-status-changed', {
                action: 'updated',
                showUnfinishedCount: this.showUnfinishedCount
              })
              console.log('[StudentGrid] 已发出 student-status-changed 事件')
            })
      }
    },
    onRecordPressStart(student, event) {
      // 清除之前的定时器
      this.cancelLongPress()

      this.selectedStudent = student
      // 重置长按标志
      this.isLongPressTriggered = false

      // 保存鼠标位置
      this.mousePosition = {
        x: event.clientX || (event.touches && event.touches[0].clientX),
        y: event.clientY || (event.touches && event.touches[0].clientY)
      }

      // 设置0.5秒后触发长按事件的定时器
      this.longPressTimer = setTimeout(() => {
        this.isLongPressTriggered = true // 设置长按标志
        // 长按直接触发彻查作业，不显示弹出菜单
        this.checkStudentHistory()
      }, 500)
      
      this.$emit('record-press-start', { student, event })
    },
    onRecordPressEnd(event) {
      // 取消长按事件
      this.cancelLongPress()
      
      this.$emit('record-press-end', event)
    },
    // 取消长按事件
    cancelLongPress() {
      clearTimeout(this.longPressTimer)
      this.isLongPressTriggered = false
    },
    // 显示右键菜单
    onShowContextMenu(event, student) {
      event.preventDefault()  // 阻止浏览器默认右键菜单
      event.stopPropagation() // 阻止事件冒泡

      this.selectedStudent = student

      // 清除长按检测
      this.cancelLongPress()

      // 根据配置决定是否显示右键菜单
      if (this.showContextMenu) {
        // 保存鼠标位置
        this.mousePosition = {
          x: event.clientX,
          y: event.clientY
        }

        // 显示全局Popover
        this.contextMenuVisible = true

        // 设置popover位置为鼠标位置
        this.$nextTick(() => {
          const popover = this.$refs.contextMenu
          if (popover && popover.$el) {
            const popper = popover.$el
            popper.style.position = 'fixed'
            popper.style.left = this.mousePosition.x + 'px'
            popper.style.top = this.mousePosition.y + 'px'
            popper.style.transform = 'translate(0, 0)'
            popper.style.zIndex = '9999'
          }
        })

        // 添加全局点击监听器，用于隐藏popover
        this.addGlobalClickListener()
      } else {
        // 不显示右键菜单，直接调用彻查作业
        this.checkStudentHistory()
      }
    },
    // 显示全局Popover
    showPopover() {
      // 显示全局Popover
      this.contextMenuVisible = true

      // 设置popover位置为鼠标位置
      this.$nextTick(() => {
        const popover = this.$refs.contextMenu
        if (popover && popover.$el) {
          const popper = popover.$el
          popper.style.position = 'fixed'
          popper.style.left = this.mousePosition.x + 'px'
          popper.style.top = this.mousePosition.y + 'px'
          popper.style.transform = 'translate(0, 0)'
          popper.style.zIndex = '9999'
        }
      })

      // 添加全局点击监听器，用于隐藏popover
      this.addGlobalClickListener()
    },
    // 添加全局点击监听器
    addGlobalClickListener() {
      // 创建一个函数用于隐藏popover
      const hidePopover = (event) => {
        // 检查mousedown事件是否发生在popover内部
        const popoverElements = document.querySelectorAll('.el-popover')
        let isClickInsidePopover = false
        for (let i = 0; i < popoverElements.length; i++) {
          if (popoverElements[i] === event.target || popoverElements[i].contains(event.target)) {
            isClickInsidePopover = true
            break
          }
        }

        // 检查是否是学生按钮
        const isClickOnStudentButton = event.target.classList.contains('student-btn') ||
            event.target.closest('.student-btn')

        // 只有当mousedown事件发生在popover外部且不是学生按钮时，才隐藏popover
        if (!isClickInsidePopover && !isClickOnStudentButton) {
          this.contextMenuVisible = false
          // 移除事件监听器
          document.removeEventListener('mousedown', hidePopover)
        }
      }

      // 在下一个tick添加事件监听器，避免立即触发
      this.$nextTick(() => {
        // 先移除之前的监听器（如果有的话）
        document.removeEventListener('mousedown', this.globalClickListener)
        // 保存当前监听器的引用
        this.globalClickListener = hidePopover
        // 使用mousedown事件而不是click事件
        document.addEventListener('mousedown', hidePopover)
      })
    },
    // 彻查学生作业历史
    checkStudentHistory() {
      // 只有在右键菜单触发时才需要隐藏弹出菜单
      if (this.contextMenuVisible) {
        this.contextMenuVisible = false
        // 移除全局点击监听器
        if (this.globalClickListener) {
          document.removeEventListener('mousedown', this.globalClickListener)
        }
      }
      if (this.selectedStudent) {
        // 触发事件通知父组件处理学生历史记录
        this.$emit('check-student-history', this.selectedStudent)
      }
    },
    
    // 根据作业类型获取科目ID
    getSubjectIdByHomeworkType(homeworkTypeId) {
      // 根据作业类型ID获取科目ID
      const homeworkType = this.homeworkTypes.find(type => type.id === homeworkTypeId)
      return homeworkType ? homeworkType.subjectId : 1 // 默认为语文科目
    },
    
    // 处理学生信息更新事件
    handleStudentInfoUpdated(data) {
      // 检查是否是当前班级的学生
      if (data.classId === this.classId) {
        // 重新加载学生数据
        this.reloadStudents();
      }
    },
    
    // 处理学生删除事件
    handleStudentDeleted(data) {
      // 检查是否是当前班级的学生
      if (data.classId === this.classId) {
        // 重新加载学生数据
        this.reloadStudents();
      }
    },
    
    // 处理学生添加事件
    handleStudentAdded(data) {
      // 检查是否是当前班级的学生
      if (data.classId === this.classId) {
        // 重新加载学生数据
        this.reloadStudents();
      }
    },
    
    // 处理作业记录删除事件
    handleHomeworkRecordDeleted(data) {
      // 检查是否是当前班级的作业记录
      if (data.classId === this.classId) {
        // 重新加载订正记录数据
        this.reloadCorrectionRecords();
      }
    },
    
    // 处理批量作业记录删除事件
    handleHomeworkRecordsBatchDeleted(data) {
      // 检查是否是当前班级的作业记录
      if (data.classId === this.classId) {
        // 重新加载订正记录数据
        this.reloadCorrectionRecords();
      }
    },
    
    // 重新加载订正记录数据
    async reloadCorrectionRecords() {
      if (!this.classId || !this.currentDate || !this.selectedType) {
        return;
      }
      
      try {
        const checkResponse = await this.$http.get(`/homeworkAssignments/check?classId=${this.classId}&date=${this.currentDate}&homeworkTypeId=${this.selectedType}`);
        
        if (!checkResponse.data.assigned) {
          this.$emit('update:correctionRecords', []);
          EventBus.$emit('homework-unassigned');
          return;
        }
      } catch (error) {
        console.error('检查作业布置状态失败:', error);
        return;
      }
      
      let url = `/correctionRecords?date=${this.currentDate}&typeId=${this.selectedType}`;
      if (this.classId) {
        url = `/correctionRecords?date=${this.currentDate}&typeId=${this.selectedType}&classId=${this.classId}`;
      }
      
      this.$http.get(url)
        .then(response => {
          // 通过.sync修饰符更新父组件的订正记录数据
          this.$emit('update:correctionRecords', response.data);
        })
        .catch(error => {
          console.error('重新加载订正记录数据失败:', error);
        });
    },
    
    // 重新加载学生数据
    reloadStudents() {
      let url = '/students';
      if (this.classId) {
        url = `/students/class?classId=${this.classId}`;
      }
      
      this.$http.get(url)
        .then(response => {
          // 通过.sync修饰符更新父组件的数据
          this.$emit('update:sortedStudents', response.data);
        })
        .catch(error => {
          console.error('重新加载学生数据失败:', error);
        });
    }
  },
  mounted() {
    // 监听学生信息更新事件
    EventBus.$on('student-info-updated', this.handleStudentInfoUpdated);
    // 监听学生删除事件
    EventBus.$on('student-deleted', this.handleStudentDeleted);
    // 监听学生添加事件
    EventBus.$on('student-added', this.handleStudentAdded);
    // 监听作业记录删除事件
    EventBus.$on('homework-record-deleted', this.handleHomeworkRecordDeleted);
    // 监听批量作业记录删除事件
    EventBus.$on('homework-records-batch-deleted', this.handleHomeworkRecordsBatchDeleted);
  },
  beforeDestroy() {
    // 移除事件监听器
    EventBus.$off('student-info-updated', this.handleStudentInfoUpdated);
    EventBus.$off('student-deleted', this.handleStudentDeleted);
    EventBus.$off('student-added', this.handleStudentAdded);
    EventBus.$off('homework-record-deleted', this.handleHomeworkRecordDeleted);
    EventBus.$off('homework-records-batch-deleted', this.handleHomeworkRecordsBatchDeleted);
  }
}
</script>